import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import PageHeader from "../PageHeader";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import CheckinSummary from "./CheckinSummary";
import FinishForm from "./FinishForm";
import HistoryExercise from "./HistoryExercise";
import LogSetSheet from "./LogSetSheet";
import SessionSummary from "./SessionSummary";
import { formatDay, formatTime, setLine, templateLabel } from "./lib/format";
import {
  backTo,
  exerciseGroups,
  feedbackRows,
  historyPath,
  isSessionId,
  jointWords,
  sessionTitle,
  setsWord,
  stateBadge,
  stubExercise,
} from "./lib/history";
import { failureKind, feedbackMuscles, mergeSets, withoutSet } from "./lib/sessionSets";
import { createSetQueue } from "./lib/setQueue";

const GONE = "That session isn't there any more — it may have been discarded.";

/** Move focus to the first of `selectors` that's on the page, once React has drawn it. */
const focusSoon = (...selectors) => {
  setTimeout(() => {
    for (const selector of selectors) {
      const target = document.querySelector(selector);
      if (target && !target.disabled) {
        target.focus();
        return;
      }
    }
  }, 0);
};

/**
 * One session from History (/training/history/:id; walkthroughs 7, 10):
 * its check-in, its plan against what was done (each planned exercise
 * with the sets logged for it, then anything logged off the plan), the
 * totals, and the feedback — all of it fixable here:
 *
 * - a set opens in the logging sheet (LogSetSheet, editing): change its
 *   numbers (PATCH) or delete it (asked inside the sheet);
 * - the feedback opens the finish form (FinishForm): for a finished
 *   session that saves new answers (POST finish again); for one that
 *   closed itself (D18) it finishes it as of its last activity, so the
 *   feedback counts;
 * - *Discard* deletes the session and its sets after an in-app confirm,
 *   then goes back to the list, which says so.
 *
 * An open session is shown too, but it's finished on Today. A session
 * that isn't there (discarded, or never was) says so, with the way back.
 * Reads `settings` and `exercises` from the Training layout's outlet context.
 */
function TrainingSessionDetail() {
  const { id: rawId } = useParams();
  const { me, settings, exercises } = useOutletContext() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const ids = useId();
  // The way back: where it was opened from (Progress, or History's page or
  // day). A discard always lands on History, which says what went.
  const back = backTo(location.state);
  const historyBack = historyPath(location.state?.historySearch);
  const timeZone = settings?.timezone;
  const catalog = useMemo(() => exercises ?? [], [exercises]);
  const byKey = useMemo(() => new Map(catalog.map((e) => [e.key, e])), [catalog]);
  const validId = isSessionId(rawId);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(validId);
  const [failure, setFailure] = useState(validId ? null : { gone: true });
  const [reloadTick, setReloadTick] = useState(0);
  // Render mirror: an answer builds on the newest copy, not the one its
  // request started from.
  const sessionRef = useRef(null);
  sessionRef.current = session;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!validId) {
      setSession(null);
      setLoading(false);
      setFailure({ gone: true });
      return undefined;
    }
    let alive = true;
    setSession(null);
    setLoading(true);
    setFailure(null);
    const load = async () => {
      try {
        const { data } = await trainingApi.sessions.get(Number(rawId));
        if (!alive) return;
        setSession(data);
      } catch (err) {
        if (!alive) return;
        setFailure(
          failureKind(err) === "gone"
            ? { gone: true }
            : { message: apiErrorMessage(err, "Couldn't load this session.") },
        );
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [rawId, validId, reloadTick]);

  const replaceSession = (next) => {
    sessionRef.current = next;
    setSession(next);
  };

  // --- a set: change or delete it in the logging sheet ----------------------
  // {groupId, key, name, number, editing, open}: kept (closed) after use, so
  // the sheet can close properly — its focus return runs as it unmounts.
  const [sheet, setSheet] = useState(null);
  const sheetOpener = useRef(null);
  const [setStatus, setSetStatus] = useState("");

  const openSet = (group, name, set, number) => {
    sheetOpener.current = document.activeElement;
    setSheet({ groupId: group.id, key: group.key, name, number, editing: set, open: true });
  };

  const saveSet = async (setId, body) => {
    const { data } = await trainingApi.sets.patch(sessionRef.current.id, setId, body);
    if (!mounted.current) return;
    const latest = sessionRef.current;
    replaceSession({ ...latest, sets: mergeSets(latest.sets, [data]) });
    const exercise = byKey.get(data.exercise) ?? stubExercise(data.exercise, sheet?.name);
    setSetStatus(`Saved set ${sheet?.number} of ${sheet?.name}: ${setLine(data, latest.units, exercise)}.`);
  };

  const deleteSet = async (setId) => {
    try {
      await trainingApi.sets.remove(sessionRef.current.id, setId);
    } catch (err) {
      if (failureKind(err) !== "gone") throw err; // already gone: as asked
    }
    if (!mounted.current) return;
    const latest = sessionRef.current;
    replaceSession({ ...latest, sets: withoutSet(latest.sets, setId) });
    setSetStatus(`Deleted set ${sheet?.number} of ${sheet?.name}.`);
  };

  // --- feedback: the finish form, again --------------------------------------
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState("");
  const feedbackHeading = useRef(null);

  const feedbackSaved = (data) => {
    const latest = sessionRef.current;
    const wasClosed = latest.session_state !== "finished";
    // A finish never changes the sets; the ones here are at least as new
    // (a set changed while it ran is kept).
    replaceSession({ ...latest, ...data, sets: latest.sets });
    setFeedbackOpen(false);
    // Say what was saved — the server's copy, not the button pressed: a
    // Finish with nothing answered (or every answer cleared) saved none.
    const given = Object.keys(data?.feedback ?? {}).length > 0;
    let status = "No feedback given.";
    if (given && wasClosed) status = "Feedback saved — your next plans read it.";
    else if (given) status = "Feedback saved — your next plans read the new answers.";
    setFeedbackStatus(status);
    setTimeout(() => feedbackHeading.current?.focus(), 0);
  };

  // --- discard ----------------------------------------------------------------
  const [confirming, setConfirming] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState(null);
  const discardingRef = useRef(false);

  // This person's queue for this session (none without a signed-in id).
  const queueOf = () => {
    const current = sessionRef.current;
    if (me?.id === null || me?.id === undefined || current?.id === undefined) return null;
    return createSetQueue(me.id, current.id);
  };
  /**
   * Sets this device still had queued for it go with it: sent later they
   * would 404 and be reported as "couldn't be saved" (the session view's
   * discard clears its queue too). Once mono has discarded it — whether or
   * not the person is still here.
   */
  const forgetQueued = () => queueOf()?.clear();
  const leave = () => {
    const current = sessionRef.current;
    const count = current?.sets?.length ?? 0;
    const day = formatDay(current?.started_at, timeZone);
    navigate(historyBack, {
      replace: true,
      state: {
        notice: `Discarded the ${templateLabel(current?.template ?? "custom")} session${day ? ` of ${day}` : ""}${count ? ` and its ${setsWord(count)}` : ""}.`,
      },
    });
  };
  const leaveDiscarded = () => {
    forgetQueued();
    leave();
  };

  const discard = async () => {
    if (discardingRef.current) return;
    discardingRef.current = true;
    setDiscarding(true);
    setDiscardError(null);
    // Marked as it's sent: a reload or a closed tab can lose the answer, and
    // the next Today load then finds the session gone — the mark tells it
    // the sets went with it, as asked. Mono's no unmarks; no answer (it may
    // have been a yes) leaves it.
    queueOf()?.markDiscarding();
    try {
      await trainingApi.sessions.discard(sessionRef.current.id);
    } catch (err) {
      const kind = failureKind(err);
      if (kind !== "gone") {
        if (kind !== "offline") queueOf()?.unmarkDiscarding();
        discardingRef.current = false;
        if (mounted.current) {
          setDiscardError(apiErrorMessage(err, "Couldn't discard the session."));
          setDiscarding(false);
        }
        return;
      }
      // a 404: it's already gone — as asked
    }
    forgetQueued();
    // A late answer must not pull someone who left back to the list.
    if (mounted.current) leave();
  };

  // --- render -------------------------------------------------------------------
  const backLink = (
    <Button asChild variant="ghost" className="-ml-3 h-11 self-start px-3 md:h-9">
      <Link to={back.to}>
        <ArrowLeft aria-hidden="true" />
        {back.label}
      </Link>
    </Button>
  );

  if (!session) {
    return (
      <div className="page-container mt-6 pt-4 pb-8">
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          {!failure?.gone && backLink}
          <PageHeader title="Session" className="mb-0" />
          {loading && (
            <div className="py-12 text-center" role="status">
              <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Loading the session…</span>
            </div>
          )}
          {!loading && failure?.gone && (
            <section className="flex flex-col gap-3 rounded-lg border bg-card p-4 md:p-6">
              <p className="m-0">{GONE}</p>
              <Button asChild className="h-11 w-full md:h-9 md:w-auto md:self-start">
                <Link to={back.to}>{back.label}</Link>
              </Button>
            </section>
          )}
          {!loading && failure?.message && (
            <Alert variant="destructive">
              <AlertDescription className="flex flex-wrap items-center gap-3">
                <span className="flex-1">{failure.message}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-11 md:h-8"
                  onClick={() => setReloadTick((tick) => tick + 1)}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    );
  }

  const state = session.session_state;
  const badge = stateBadge(state);
  const sets = session.sets ?? [];
  const units = session.units ?? settings?.units ?? "lb";
  const planUnits = session.plan?.units ?? units;
  const hasPlan = (session.plan?.items ?? []).length > 0;
  const groups = exerciseGroups(session);
  const exerciseOf = (key, name) => byKey.get(key) ?? stubExercise(key, name);
  const nameOf = (group) => group.item?.name ?? exerciseOf(group.key).name;
  const painReports = session.pain_reports ?? [];
  const rows = feedbackRows(session.feedback);
  const canRate = state !== "open" && feedbackMuscles(sets, byKey).length > 0;
  const started = formatTime(session.started_at, timeZone);
  const ended = formatTime(session.finished_at, timeZone);
  const subtitle = [
    started ? `Started ${started}` : null,
    state === "finished" && ended ? `finished ${ended}` : null,
    state === "auto_closed" && ended ? `closed itself at ${ended}` : null,
    state === "open" ? "still open" : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const agent = session.source && session.source !== "ui" ? session.logged_by?.username : null;

  const sheetGroup = sheet ? groups.find((group) => group.id === sheet.groupId) : null;
  const sheetExercise = sheet ? exerciseOf(sheet.key, sheet.name) : null;

  return (
    <div className="page-container mt-6 pt-4 pb-8">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        {backLink}
        <div>
          <PageHeader
            title={sessionTitle(session, timeZone)}
            subtitle={subtitle ? `${subtitle}.` : undefined}
            badge={badge.label}
            badgeVariant={badge.variant}
            className="mb-0"
          />
          {agent && (
            <p className="mt-1 mb-0 text-sm text-foreground/80">Started by {agent}, through an agent.</p>
          )}
        </div>

        {/* role="status": page content read on load, not an event — the
            Alert primitive's default role="alert" announced it over the title. */}
        {state === "auto_closed" && (
          <Alert role="status">
            <AlertDescription className="text-foreground">
              This session closed itself: nothing was logged for 6 hours, so it ended at its last
              activity without feedback, and its muscles kept their weekly sets. Add feedback below to
              make it count.
            </AlertDescription>
          </Alert>
        )}
        {state === "open" && (
          <Alert role="status">
            <AlertDescription className="flex flex-wrap items-center gap-3 text-foreground">
              <span className="flex-1">Still open — log its sets and finish it on Today.</span>
              <Button asChild size="sm" className="h-11 md:h-8">
                <Link to="/training">Go to Today</Link>
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <section aria-labelledby={`${ids}-checkin`} className="rounded-lg border bg-card p-4">
          <h2 id={`${ids}-checkin`} className="mt-0 mb-2 font-display text-xl font-semibold">
            Check-in
          </h2>
          <CheckinSummary checkin={session.checkin} />
        </section>

        {/* The session's pain reports, oldest first (the check-in's and the
            ones made during it) — the pain dialog promises its note shows
            here. */}
        {painReports.length > 0 && (
          <section aria-labelledby={`${ids}-pain`} className="flex flex-col gap-2 rounded-lg border bg-card p-4">
            <h2 id={`${ids}-pain`} className="m-0 font-display text-xl font-semibold">
              Pain reported
            </h2>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {painReports.map((report) => {
                const time = formatTime(report.reported_at, timeZone);
                const during = report.exercise ? exerciseOf(report.exercise).name : null;
                return (
                  <li key={report.id}>
                    <p className="m-0">
                      <span className="font-medium">{jointWords(report)}</span>
                      {during ? ` during ${during}` : ""}
                      {report.from_checkin ? ", from the check-in" : ""}
                      {time && (
                        <>
                          {" · "}
                          <time dateTime={report.reported_at} className="tabular-nums">
                            {time}
                          </time>
                        </>
                      )}
                    </p>
                    {report.note && (
                      <p className="m-0 text-sm break-words text-foreground/80">“{report.note}”</p>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="m-0 text-xs text-muted-foreground">
              Exercises that load a joint in pain are left out of your plans for 7 days after a report,
              unless a later check-in says it&apos;s fine.
            </p>
          </section>
        )}

        <section aria-labelledby={`${ids}-plan`} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2
              id={`${ids}-plan`}
              tabIndex={-1}
              data-plan-heading
              className="m-0 rounded-sm font-display text-xl font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            >
              {hasPlan ? "Plan and what was done" : "What was done"}
            </h2>
            <p className="m-0 text-sm text-foreground/80">
              {hasPlan ? session.plan.reason || "The plan as it stood when the session ended." : "No plan — built as you went."}
              {sets.length > 0 ? " Tap a set to change its numbers or delete it." : ""}
            </p>
            <p role="status" className="m-0 text-sm text-foreground/80">
              {setStatus}
            </p>
          </div>
          {groups.length === 0 ? (
            <p className="m-0 rounded-lg border border-dashed bg-card p-4 text-sm text-muted-foreground">
              Nothing was planned or logged in this session.
            </p>
          ) : (
            <ol aria-label="Exercises" className="m-0 flex list-none flex-col gap-3 p-0">
              {groups.map((group) => {
                const name = nameOf(group);
                return (
                  <HistoryExercise
                    key={group.id}
                    groupId={group.id}
                    name={name}
                    item={group.item}
                    exercise={byKey.get(group.key)}
                    sets={group.sets}
                    units={units}
                    planUnits={planUnits}
                    offPlan={hasPlan && !group.item}
                    timeZone={timeZone}
                    onEditSet={(set, number) => openSet(group, name, set, number)}
                  />
                );
              })}
            </ol>
          )}
        </section>

        {sets.length > 0 && <SessionSummary session={session} exercises={catalog} past />}

        <div className="flex flex-col gap-3">
          {/* Open, the form stands alone: it carries its own heading
              ("Feedback", or "Finish the session" for one that closed
              itself). */}
          {feedbackOpen ? (
            <FinishForm
              session={{ ...session, can_discard: false }}
              exercises={catalog}
              onFinished={feedbackSaved}
              onDiscarded={leaveDiscarded}
              onCancel={() => {
                setFeedbackOpen(false);
                focusSoon("[data-action=feedback]");
              }}
              cancelLabel={state === "finished" ? "Keep it as it was" : "Not now"}
              fromHistory
            />
          ) : (
            <section aria-labelledby={`${ids}-feedback`} className="flex flex-col gap-3">
              <h2
                ref={feedbackHeading}
                id={`${ids}-feedback`}
                tabIndex={-1}
                className="m-0 rounded-sm font-display text-xl font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
              >
                Feedback
              </h2>
              <div className="flex flex-col gap-3 rounded-lg border bg-card p-4">
                {rows.length > 0 ? (
                  <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                    {rows.map((row) => (
                      <div key={row.key} className="contents">
                        <dt className="font-medium">{row.label}</dt>
                        <dd className="m-0">{row.text}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="m-0 text-sm text-muted-foreground">
                    {state === "open"
                      ? "Given when you finish it on Today."
                      : state === "auto_closed"
                        ? "None — it closed itself before it was finished."
                        : "None given, so each muscle kept its weekly sets."}
                  </p>
                )}
                {state !== "open" && !canRate && (
                  <p className="m-0 text-sm text-muted-foreground">
                    No muscle got 2 or more sets in this session, so there&apos;s nothing to rate.
                  </p>
                )}
                {canRate && (
                  <div className="flex flex-col gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 w-full md:h-9 md:w-auto md:self-start"
                      data-action="feedback"
                      onClick={() => {
                        setFeedbackStatus("");
                        setFeedbackOpen(true);
                      }}
                    >
                      {state === "finished" && rows.length > 0 ? "Change feedback…" : "Add feedback…"}
                    </Button>
                    <p className="m-0 text-xs text-muted-foreground">
                      {state === "finished"
                        ? "The pump and workload for each muscle with 2 or more sets; saving replaces what's here, and your next plans read it."
                        : "The pump and workload for each muscle with 2 or more sets. Finishing it now counts it as finished at its last activity, so the answers count toward your next plans."}
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}
          {/* Always on the page, so the outcome is announced when it lands. */}
          <p role="status" className="m-0 text-sm text-foreground/80">
            {feedbackStatus}
          </p>
        </div>

        {session.can_discard && (
          <div className="flex flex-col gap-1 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              className="h-11 w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive md:h-9 md:w-auto md:self-start"
              onClick={() => {
                setDiscardError(null);
                setConfirming(true);
              }}
            >
              <Trash2 aria-hidden="true" />
              Discard this session…
            </Button>
            <p className="m-0 text-xs text-foreground/80">
              Deletes it{sets.length ? ` and its ${setsWord(sets.length)}` : ""}, so nothing from it
              counts toward your plans. You&apos;re asked first.
            </p>
          </div>
        )}

        <AlertDialog open={confirming} onOpenChange={(open) => !discarding && setConfirming(open)}>
          {/* data-training: portalled out of the layout's wrapper, so it
              carries the scope that keeps its muted text AA (tailwind.css). */}
          <AlertDialogContent
            data-training=""
            onEscapeKeyDown={(event) => discarding && event.preventDefault()}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Discard this session?</AlertDialogTitle>
              <AlertDialogDescription>
                {sets.length > 0
                  ? `Its ${sets.length} logged ${sets.length === 1 ? "set goes" : "sets go"} with it, and nothing from it counts toward your plans.`
                  : "Nothing from it counts toward your plans."}{" "}
                This can&apos;t be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {discardError && (
              <p role="alert" className="m-0 text-sm text-destructive">
                {discardError}
              </p>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel className="h-11 md:h-9" disabled={discarding}>
                Keep it
              </AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                className="h-11 md:h-9"
                disabled={discarding}
                onClick={(event) => {
                  event.preventDefault();
                  discard();
                }}
              >
                {discarding && <Loader2 className="animate-spin" aria-hidden="true" />}
                {discarding ? "Discarding…" : "Discard session"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {sheetExercise && (
          <LogSetSheet
            open={sheet.open}
            onOpenChange={(open) => setSheet((current) => (current ? { ...current, open } : current))}
            exercise={sheetExercise}
            item={sheetGroup?.item ?? null}
            sets={sheetGroup?.sets ?? []}
            settings={settings}
            units={units}
            editing={sheet.editing}
            onSave={saveSet}
            onDelete={deleteSet}
            onCloseAutoFocus={(event) => {
              // A deleted set's row is gone with it: land on the exercise's
              // first set, or its name — or, when its last set took an
              // off-plan exercise with it, the section — instead of <body>.
              const opener = sheetOpener.current;
              if (opener && opener.isConnected && !opener.disabled) return;
              event.preventDefault();
              focusSoon(
                `[data-group="${sheet.groupId}"] [data-set-row]`,
                `[data-group="${sheet.groupId}"] [data-heading]`,
                "[data-plan-heading]",
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

export default TrainingSessionDetail;
